import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";

/**
 * DemoLoop — animation pédagogique courte, sans GIF.
 *
 * Affiche une suite d'« étapes » en boucle (1.6s par étape).
 * Chaque étape : { title, body, icon, color } (color = tailwind text-xxx).
 *
 * Conçu pour rester léger : CSS pur, pas de framer-motion.
 *
 * Usage:
 *   <DemoLoop steps={[
 *     { title: "Notez votre vin", body: "Tapez en langage libre…", icon: PenLine, color: "text-[#1F3A5F]" },
 *     { title: "geoVINUM AI extrait", body: "Cuvée, millésime, robe, nez…", icon: Sparkles, color: "text-[#E5276A]" },
 *     { title: "Enrichissement web", body: "Photo, domaine, prix indic.", icon: Globe, color: "text-[#1F3A5F]" },
 *     { title: "Carnet sauvegardé", body: "Tu retrouves tout en 1 clic.", icon: BookOpen, color: "text-[#E5276A]" },
 *   ]} />
 */
export default function DemoLoop({ steps = [], interval = 1700, dense = false, className = "", testId = "demo-loop" }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!steps.length) return undefined;
    const t = setInterval(() => setI((cur) => (cur + 1) % steps.length), interval);
    return () => clearInterval(t);
  }, [steps.length, interval]);

  if (!steps.length) return null;
  const current = steps[i];
  const Icon = current.icon;

  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border border-dashed border-[#1F3A5F]/15 bg-gradient-to-br from-[#FBF7F3] via-white to-[#FCE4ED]/30 ${dense ? "p-3" : "p-4 md:p-5"} ${className}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center transition-all duration-500 ${current.color || "text-[#E5276A]"}`}>
          {Icon ? <Icon className="w-5 h-5" /> : <span className="text-lg font-bold">{i + 1}</span>}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div
            key={i}
            className="text-sm font-bold text-[#1F3A5F] animate-[fade-up_0.4s_ease-out]"
          >
            {current.title}
          </div>
          <div
            key={`b-${i}`}
            className="text-xs text-[#4A5568] truncate animate-[fade-up_0.5s_ease-out]"
          >
            {current.body}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#8B95A1] shrink-0" />
      </div>
      {/* Progress dots */}
      <div className="mt-3 flex items-center gap-1.5">
        {steps.map((s, idx) => (
          <div
            key={idx}
            className={`h-1 rounded-full transition-all duration-300 ${idx === i ? "bg-[#E5276A] w-8" : "bg-[#1F3A5F]/12 w-3"}`}
          />
        ))}
      </div>
      <style>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
